import React, { useContext, useEffect } from 'react';
import { func } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

// components
import { Drawer } from '@mui/material';
import SelfAuthorizationConfirmationDialog from 'feature/loading-management/self-authorizations/product-confirmation-dialog/ProductConfirmationDialog.connector';
import ForceCloseDialog from 'feature/loading-management/supplier-authorizations/confirmation-dialog/ConfirmationDialog.connector';

// utils
import { DRAWER_TYPES } from 'utils/constants';

// Context
import { AuthContext } from 'authentication/AuthContext';
import { DrawerContext } from './DrawerContext';

const DrawerContainer = ({
  fetchNotifications,
  fetchOpenPTOs,
  fetchSearchPTOs,
  setForceClose,
  setForceCloseCustomerDialog,
}) => {
  const { affiliateRoles, roles: inputRoles } = useContext(AuthContext);
  const {
    componentProps: {
      Component,
      drawerPaperSx,
      type,
      holdOpenSelector,
      forceCloseSelector,
      refreshNotifications,
      resetWizard,
      setCurrentDialogOpen,
    },
    setDrawerComponentProps,
  } = useContext(DrawerContext);
  const dispatch = useDispatch();

  const holdDrawerOpen = useSelector(holdOpenSelector);
  const forceDrawerClose = useSelector(forceCloseSelector);

  const handleDrawerClose = () => {
    if (holdDrawerOpen && !forceDrawerClose) {
      type === DRAWER_TYPES.createNewSelfAuth
        ? dispatch(setCurrentDialogOpen('batchActivate'))
        : dispatch(setCurrentDialogOpen());
      return;
    }
    type === DRAWER_TYPES.createNewSelfAuth && dispatch(setCurrentDialogOpen(''));
    // If refreshNotifications is passed in as Drawer prop, will re-fetch Notifications upon drawer close
    // Allows to selectively refresh Notifications if Drawer opening is the result of a Notification click
    if (refreshNotifications) {
      const currentRoles = [...inputRoles, ...affiliateRoles];
      fetchNotifications(currentRoles);
    }
    resetWizard && dispatch(resetWizard());

    setDrawerComponentProps({
      Component: '',
      drawerPaperSx: undefined,
      forceCloseSelector: () => false,
      holdOpenSelector: () => false,
      refreshNotifications: false,
      resetWizard: undefined,
      setCurrentDialogOpen: () => {},
      type: '',
    });
  };

  useEffect(() => {
    if (forceDrawerClose) {
      handleDrawerClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceDrawerClose]);

  const handleRefresh = () => {
    fetchOpenPTOs();
    fetchSearchPTOs();
  };

  const extraComponentProps = {
    [DRAWER_TYPES.createNewPto]: {
      refreshCall: handleRefresh,
    },
    [DRAWER_TYPES.copyPto]: {
      refreshCall: handleRefresh,
    },
    [DRAWER_TYPES.editPto]: {
      refreshCall: handleRefresh,
    },
    [DRAWER_TYPES.createNewCarrierAuth]: {
      confirmCloseDialog: () => setForceClose(true),
    },
    [DRAWER_TYPES.createNewCustomerAuth]: {
      confirmCloseDialog: () => setForceCloseCustomerDialog(true),
    },
  };

  return (
    Component && (
      <>
        <Drawer
          anchor="right"
          onClose={handleDrawerClose}
          open
          slotProps={{
            paper: {
              sx: {
                overflowY: 'revert',
                width: { sm: 390, xs: '100%' },
                ...drawerPaperSx,
              },
            },
          }}
        >
          <Component onClose={handleDrawerClose} {...extraComponentProps[type]} />
        </Drawer>
        {type && (
          <SelfAuthorizationConfirmationDialog
            handleCloseDialog={() => dispatch(setCurrentDialogOpen(''))}
          />
        )}
        {type && <ForceCloseDialog {...extraComponentProps[type]} />}
      </>
    )
  );
};

DrawerContainer.propTypes = {
  fetchNotifications: func,
  fetchOpenPTOs: func,
  fetchSearchPTOs: func,
  setForceClose: func,
  setForceCloseCustomerDialog: func,
};

DrawerContainer.defaultProps = {
  fetchNotifications: () => {},
  fetchOpenPTOs: () => {},
  fetchSearchPTOs: () => {},
  setForceClose: () => {},
  setForceCloseCustomerDialog: () => {},
};

export default DrawerContainer;
